import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Camera } from "lucide-react";
import { GALLERY } from "../data/mock";

export default function Gallery() {
  const [active, setActive] = useState(null);

  return (
    <div data-testid="gallery-page" className="bg-[#3915AA]">
      {/* Header */}
      <section className="section-padding container-custom">
        <div className="max-w-3xl">
          <p className="text-[#C9BCFF] uppercase tracking-[0.25em] text-xs mb-4">Gallery</p>
          <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-[1.02]">
            Glow-ups, <span className="text-gradient-primary">tail wags</span>, and pure joy.
          </h1>
          <p className="mt-6 text-white/70 text-lg leading-relaxed max-w-2xl">
            A peek at the pets who walk into our studio — and the happy fluff-balls that walk out.
          </p>
        </div>
      </section>

      {/* Masonry-like grid */}
      <section className="container-custom pb-24">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {GALLERY.map((g, i) => (
            <button
              key={i}
              data-testid={`gallery-item-${i}`}
              onClick={() => setActive(g)}
              className={`group relative rounded-2xl overflow-hidden border border-white/10 ${
                i % 5 === 0 ? "row-span-2 aspect-[3/5]" : "aspect-[4/5]"
              }`}
            >
              <img
                src={g.src}
                alt={g.title}
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1F0A66]/85 via-transparent to-transparent" />
              <div className="absolute bottom-3 left-3 right-3 text-left">
                <p className="text-white font-heading font-semibold text-base md:text-lg leading-tight">
                  {g.title}
                </p>
                <p className="text-white/60 text-xs mt-0.5">{g.pet}</p>
              </div>
              <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity bg-[#FC5106] rounded-full p-2">
                <Camera className="w-4 h-4 text-white" />
              </div>
            </button>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="container-custom pb-24">
        <div className="rounded-[32px] border border-white/10 bg-[#2E0F94] p-10 md:p-14 text-center grain relative overflow-hidden">
          <div className="absolute -top-12 -right-12 w-64 h-64 bg-[#FC5106]/15 blur-3xl rounded-full" />
          <div className="absolute -bottom-12 -left-12 w-72 h-72 bg-white/15 blur-3xl rounded-full" />
          <div className="relative">
            <h2 className="font-heading text-3xl md:text-5xl font-bold text-white leading-tight mb-4">
              Want your pet on this wall?
            </h2>
            <p className="text-white/70 max-w-xl mx-auto mb-7">
              Book a grooming session at our Sailashree Vihar studio or get doorstep grooming
              anywhere in Bhubaneswar.
            </p>
            <Link to="/contact" data-testid="gallery-book-cta" className="btn-primary">
              Book grooming <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {active && (
        <div
          className="fixed inset-0 z-[60] bg-[#1F0A66]/90 backdrop-blur-md flex items-center justify-center p-4"
          onClick={() => setActive(null)}
          data-testid="gallery-lightbox"
        >
          <div
            className="relative max-w-4xl w-full rounded-3xl overflow-hidden border border-white/10"
            onClick={(e) => e.stopPropagation()}
          >
            <img src={active.src} alt={active.title} className="w-full max-h-[80vh] object-cover" />
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-[#1F0A66] via-[#1F0A66]/70 to-transparent p-6">
              <p className="text-white font-heading font-bold text-xl">{active.title}</p>
              <p className="text-white/60 text-sm">{active.pet}</p>
            </div>
            <button
              onClick={() => setActive(null)}
              data-testid="gallery-close"
              className="absolute top-4 right-4 bg-white/10 hover:bg-white/20 backdrop-blur-md rounded-full p-2 text-white"
              aria-label="Close"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
